import React from 'react';
import { Button } from '../ui/Button';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Logo } from '../icons/Logo';
import { DiamondIcon } from '../icons/Icons';
import UserProfile from '../UserProfile';
import { useAuthStore } from '../../stores/useAuthStore';
import { MARKETING_NS } from '../../i18n/marketingSegment';
import { MARKETING_NAV, isMarketingNavActive } from './navigation';
import MarketingNavIcon from './MarketingNavIcon';

interface MarketingSidebarProps {
  isMobileOpen?: boolean;
  onCloseMobile?: () => void;
  /** Saldo já buscado (e atualizado a cada 30 s) pela Sidebar principal. */
  credits: number | null;
  onUpgrade: () => void;
  isLightMode: boolean;
  onToggleTheme: () => void;
}

const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || 'S';

/**
 * Menu lateral da plataforma Marketing (protótipo "Snapfy Ads"): logo, bloco do
 * workspace (empresa do usuário), grupos Geral · Criar · Inteligência · Marca ·
 * Entrega, e no rodapé créditos reais + perfil + Claro/Escuro. Mesma mecânica
 * de gaveta no mobile que a Sidebar do Fashion.
 */
const MarketingSidebar: React.FC<MarketingSidebarProps> = ({
  isMobileOpen,
  onCloseMobile,
  credits,
  onUpgrade,
  isLightMode,
  onToggleTheme,
}) => {
  const { t, i18n } = useTranslation(MARKETING_NS);
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const closeMobile = () => onCloseMobile?.();

  const workspaceName = user?.company?.trim() || user?.name || 'Snapfy';
  const numberFormat = new Intl.NumberFormat(i18n.language);

  return (
    <>
      {isMobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm" onClick={closeMobile} />
      )}
      <aside
        className={`fixed lg:sticky top-0 lg:top-3.5 left-0 lg:ml-3.5 h-screen lg:h-[calc(100vh-28px)] z-50 w-64 md:w-72 lg:w-64 flex flex-col p-3 md:p-4 bg-snapfy-surface backdrop-blur-xl border border-snapfy-border lg:rounded-glass shadow-glass transition-transform duration-300 ${
          isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="mb-4 flex items-center justify-between px-1.5 flex-shrink-0">
          <Link to="/marketing" onClick={closeMobile}>
            <Logo className="h-6 w-auto" />
          </Link>
          <button
            type="button"
            onClick={closeMobile}
            aria-label={t('sidebar.close')}
            className="lg:hidden p-1.5 text-snapfy-text-secondary hover:text-snapfy-text-primary"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Workspace — hoje a empresa do usuário; vira o seletor de clientes da agência quando houver. */}
        <div className="mb-3 flex items-center gap-2.5 p-2 rounded-2xl border border-snapfy-border bg-snapfy-inner flex-shrink-0">
          <span className="w-[34px] h-[34px] rounded-[10px] grid place-items-center bg-snapfy-gradient text-white text-[13px] font-bold flex-none">
            {initialsOf(workspaceName)}
          </span>
          <span className="min-w-0 flex flex-col gap-0.5">
            <span className="text-[13.5px] font-semibold leading-none text-snapfy-text-primary truncate">{workspaceName}</span>
            <span className="text-[11px] leading-none text-snapfy-text-secondary">{t('sidebar.workspaceSubtitle')}</span>
          </span>
        </div>

        <nav className="flex-1 min-h-0 overflow-y-auto custom-scrollbar pr-1">
          {MARKETING_NAV.map((group) => (
            <div key={group.id} className="mb-2">
              <h3 className="px-2.5 pt-2 pb-1.5 font-mono text-[10px] font-medium leading-none uppercase tracking-[0.08em] text-snapfy-text-secondary/70">
                {t(`sidebar.groups.${group.id}`)}
              </h3>
              <div className="space-y-0.5">
                {group.items.map((item) => {
                  const label = t(`sidebar.items.${item.id}`);
                  if (!item.to) {
                    return (
                      <span
                        key={item.id}
                        aria-disabled="true"
                        title={t('sidebar.comingSoon')}
                        className="flex items-center gap-3 h-[34px] px-2.5 rounded-[10px] text-[13px] font-medium text-snapfy-text-secondary/55 cursor-default select-none"
                      >
                        <MarketingNavIcon name={item.icon} />
                        <span className="flex-1 truncate">{label}</span>
                        <span className="text-[9.5px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-snapfy-inner text-snapfy-text-secondary/80">
                          {t('sidebar.comingSoon')}
                        </span>
                      </span>
                    );
                  }
                  const active = isMarketingNavActive(item, location.pathname);
                  return (
                    <Link
                      key={item.id}
                      to={item.to}
                      onClick={closeMobile}
                      aria-current={active ? 'page' : undefined}
                      className={`flex items-center gap-3 h-[34px] px-2.5 rounded-[10px] text-[13px] transition-colors duration-150 focus-visible:outline-none focus-visible:ring-snapfy-focus ${
                        active
                          ? 'bg-snapfy-primary-20 text-snapfy-text-primary font-semibold ring-1 ring-inset ring-snapfy-border-strong'
                          : 'text-snapfy-text-secondary font-medium hover:bg-snapfy-surface-hover hover:text-snapfy-text-primary'
                      }`}
                    >
                      <MarketingNavIcon name={item.icon} className={active ? 'text-snapfy-accent-soft' : ''} />
                      <span className="flex-1 truncate">{label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="mt-3 flex flex-col gap-2 flex-shrink-0">
          {/* Créditos reais da conta (o mesmo saldo do Fashion). */}
          <div className="p-3 rounded-2xl border border-snapfy-border bg-snapfy-inner">
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-[10px] font-medium uppercase tracking-[0.08em] text-snapfy-text-secondary">
                {t('sidebar.credits')}
              </span>
              <span className="inline-flex items-center gap-1 text-[13px] font-semibold text-snapfy-text-primary [&_svg]:w-3 [&_svg]:h-3 [&_svg]:text-snapfy-accent-soft">
                <DiamondIcon />
                {typeof credits === 'number' ? numberFormat.format(credits) : t('sidebar.creditsLoading')}
              </span>
            </div>
            {!(user?.companyId && user?.companyRole !== 'admin') && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mt-2 w-full"
                onClick={() => {
                  onUpgrade();
                  closeMobile();
                }}
              >
                {t('sidebar.buyCredits')}
              </Button>
            )}
          </div>

          <UserProfile onOpenUpgradeModal={onUpgrade} />

          {/* Claro | Escuro, como no rodapé do protótipo. */}
          <div className="flex gap-1.5 p-1 rounded-xl bg-snapfy-inner border border-snapfy-border">
            {(['light', 'dark'] as const).map((mode) => {
              const on = mode === 'light' ? isLightMode : !isLightMode;
              return (
                <button
                  key={mode}
                  type="button"
                  aria-pressed={on}
                  onClick={() => {
                    if (!on) onToggleTheme();
                  }}
                  className={`flex-1 h-[30px] rounded-button text-xs font-medium transition-colors ${
                    on ? 'bg-snapfy-primary-20 text-snapfy-text-primary ring-1 ring-inset ring-snapfy-border-strong' : 'text-snapfy-text-secondary hover:text-snapfy-text-primary'
                  }`}
                >
                  {mode === 'light' ? t('sidebar.themeLight') : t('sidebar.themeDark')}
                </button>
              );
            })}
          </div>
        </div>
      </aside>
    </>
  );
};

export default MarketingSidebar;
